import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import {
  useIntegrations,
  useCreateIntegration,
  useUpdateIntegration,
  CreateIntegrationData,
  Integration,
} from '@/hooks/useIntegrations'
import Step1BasicInfo from './wizard/Step1BasicInfo'
import Step2IntentRouting from './wizard/Step2IntentRouting'
import Step3Configuration from './wizard/Step3Configuration'

export interface WizardFormData {
  integration_type: 'openai_compatible' | 'generic_api' | 'crawler' | 'sro'
  name: string
  description: string
  is_active: boolean
  routing_examples: string
  endpoint_url: string
  api_key: string
  auth_header_type: 'bearer' | 'x-api-key' | 'api-key' | 'custom'
  custom_auth_header_name: string
  custom_headers: string
  requires_session: boolean
  session_key_name: string
  session_end_strategy: 'timeout' | 'keyword' | 'response_field'
  session_timeout_seconds: number
  session_end_keywords: string
  session_end_response_field: string
  response_json_path: string
  crawler_include_domains: string
  crawler_exclude_domains: string
  crawler_max_results: number
  crawler_search_depth: 'basic' | 'advanced'
  crawler_topic: 'general' | 'news' | 'finance'
  crawler_time_range: '' | 'day' | 'week' | 'month' | 'year'
  crawler_include_answer: 'off' | 'basic' | 'advanced'
  crawler_include_raw_content: boolean
  crawler_include_images: boolean
  crawler_include_image_descriptions: boolean
  crawler_country: string
  crawler_query_prefix: string
  sro_config_id: number | null
}

const STEPS = [
  { number: 1, title: 'Type & Info', description: 'Select integration type' },
  { number: 2, title: 'Intent Routing', description: 'Define routing examples' },
  { number: 3, title: 'Configuration', description: 'Configure connection' },
]

export default function IntegrationWizard() {
  const navigate = useNavigate()
  const { id: assistantId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const integrationId = searchParams.get('id')
  const { toast } = useToast()

  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<WizardFormData>({
    integration_type: 'openai_compatible',
    name: '',
    description: '',
    is_active: true,
    routing_examples: '',
    endpoint_url: '',
    api_key: '',
    auth_header_type: 'bearer',
    custom_auth_header_name: '',
    custom_headers: '',
    requires_session: true,
    session_key_name: 'session_id',
    session_end_strategy: 'response_field',
    session_timeout_seconds: 300,
    session_end_keywords: '',
    session_end_response_field: 'session_complete',
    response_json_path: '',
    crawler_include_domains: '',
    crawler_exclude_domains: '',
    crawler_max_results: 5,
    crawler_search_depth: 'advanced',
    crawler_topic: 'general',
    crawler_time_range: 'year',
    crawler_include_answer: 'basic',
    crawler_include_raw_content: false,
    crawler_include_images: false,
    crawler_include_image_descriptions: false,
    crawler_country: 'united states',
    crawler_query_prefix: '',
    sro_config_id: null,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const { data: integrations } = useIntegrations(assistantId || '')
  const createIntegration = useCreateIntegration()
  const updateIntegration = useUpdateIntegration()

  // Load existing integration data for editing
  useEffect(() => {
    if (integrationId && integrations) {
      const integration = integrations.find((i: Integration) => i.id === parseInt(integrationId))
      if (integration) {
        setFormData({
          integration_type: integration.integration_type,
          name: integration.name,
          description: integration.description || '',
          is_active: integration.is_active,
          routing_examples: integration.routing_examples,
          endpoint_url: integration.endpoint_url || '',
          api_key: '',
          auth_header_type: integration.auth_header_type as WizardFormData['auth_header_type'],
          custom_auth_header_name: integration.custom_auth_header_name || '',
          custom_headers: integration.custom_headers || '',
          requires_session: integration.requires_session,
          session_key_name: integration.session_key_name || 'session_id',
          session_end_strategy: integration.session_end_strategy as WizardFormData['session_end_strategy'],
          session_timeout_seconds: integration.session_timeout_seconds || 300,
          session_end_keywords: integration.session_end_keywords || '',
          session_end_response_field: integration.session_end_response_field || 'session_complete',
          response_json_path: integration.response_json_path || '',
          crawler_include_domains: integration.crawler_include_domains || '',
          crawler_exclude_domains: integration.crawler_exclude_domains || '',
          crawler_max_results: integration.crawler_max_results || 5,
          crawler_search_depth: integration.crawler_search_depth || 'advanced',
          crawler_topic: integration.crawler_topic || 'general',
          crawler_time_range: integration.crawler_time_range || 'year',
          crawler_include_answer: integration.crawler_include_answer || 'basic',
          crawler_include_raw_content: integration.crawler_include_raw_content || false,
          crawler_include_images: integration.crawler_include_images || false,
          crawler_include_image_descriptions: integration.crawler_include_image_descriptions || false,
          crawler_country: integration.crawler_country || 'united states',
          crawler_query_prefix: integration.crawler_query_prefix || '',
          sro_config_id: integration.sro_config_id || null,
        })
      }
    }
  }, [integrationId, integrations])

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    if (step === 1) {
      if (!['crawler', 'sro'].includes(formData.integration_type) && !formData.name.trim()) {
        newErrors.name = 'Integration name is required'
      }
    }

    if (step === 2) {
      if (!formData.routing_examples.trim()) {
        newErrors.routing_examples = 'At least one routing example is required'
      }
    }

    if (step === 3) {
      if (!['crawler', 'sro'].includes(formData.integration_type)) {
        if (!formData.endpoint_url.trim()) {
          newErrors.endpoint_url = 'API endpoint URL is required'
        } else {
          try {
            new URL(formData.endpoint_url)
          } catch {
            newErrors.endpoint_url = 'Enter a valid URL'
          }
        }

        if (!integrationId && !formData.api_key.trim()) {
          newErrors.api_key = 'API key is required for new integrations'
        }

        if (formData.integration_type === 'generic_api' && !formData.response_json_path.trim()) {
          newErrors.response_json_path = 'Response JSON path is required for generic API'
        }

        if (formData.auth_header_type === 'custom' && !formData.custom_auth_header_name.trim()) {
          newErrors.custom_auth_header_name = 'Custom header name is required'
        }

        if (formData.requires_session && !formData.session_key_name.trim()) {
          newErrors.session_key_name = 'Session key name is required'
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    setCurrentStep(currentStep - 1)
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return

    try {
      const isNoEndpoint = ['crawler', 'sro'].includes(formData.integration_type)
      const autoName = formData.integration_type === 'crawler' 
        ? 'Web Crawler' 
        : formData.integration_type === 'sro' 
          ? 'Service Requests' 
          : formData.name.trim()
      const autoDesc = formData.integration_type === 'crawler' 
        ? 'Web crawler for real-time search' 
        : formData.integration_type === 'sro'
          ? 'Built-in service request orchestrator'
          : formData.description.trim()

      const payload: CreateIntegrationData = {
        integration_type: formData.integration_type,
        name: autoName,
        description: autoDesc,
        is_active: formData.is_active,
        routing_examples: formData.routing_examples.trim(),
        endpoint_url: !isNoEndpoint ? formData.endpoint_url.trim() : '',
        api_key: !isNoEndpoint ? formData.api_key.trim() : '',
        auth_header_type: !isNoEndpoint ? formData.auth_header_type : 'bearer',
        custom_auth_header_name: formData.auth_header_type === 'custom' 
          ? formData.custom_auth_header_name.trim() 
          : '',
        custom_headers: !isNoEndpoint ? formData.custom_headers.trim() : '',
        is_openai_compatible: formData.integration_type === 'openai_compatible',
        response_json_path: formData.integration_type === 'generic_api' 
          ? formData.response_json_path.trim() 
          : '',
        requires_session: !isNoEndpoint ? formData.requires_session : false,
        session_key_name: formData.requires_session ? formData.session_key_name.trim() : '',
        session_end_strategy: formData.requires_session ? formData.session_end_strategy : 'response_field',
        session_timeout_seconds: formData.requires_session ? formData.session_timeout_seconds : 300,
        session_end_keywords: formData.session_end_strategy === 'keyword' 
          ? formData.session_end_keywords.trim() 
          : '',
        session_end_response_field: formData.session_end_strategy === 'response_field'
          ? formData.session_end_response_field.trim()
          : 'session_complete',
        crawler_include_domains: formData.integration_type === 'crawler' 
          ? formData.crawler_include_domains.trim() 
          : '',
        crawler_exclude_domains: formData.integration_type === 'crawler' 
          ? formData.crawler_exclude_domains.trim() 
          : '',
        crawler_max_results: formData.integration_type === 'crawler' ? formData.crawler_max_results : 5,
        crawler_search_depth: formData.integration_type === 'crawler' ? formData.crawler_search_depth : 'advanced',
        crawler_topic: formData.integration_type === 'crawler' ? formData.crawler_topic : 'general',
        crawler_time_range: formData.integration_type === 'crawler' ? formData.crawler_time_range : 'year',
        crawler_include_answer: formData.integration_type === 'crawler' ? formData.crawler_include_answer : 'basic',
        crawler_include_raw_content: formData.integration_type === 'crawler' ? formData.crawler_include_raw_content : false,
        crawler_include_images: formData.integration_type === 'crawler' ? formData.crawler_include_images : false,
        crawler_include_image_descriptions: formData.integration_type === 'crawler' ? formData.crawler_include_image_descriptions : false,
        crawler_country: formData.integration_type === 'crawler' ? formData.crawler_country : 'united states',
        crawler_query_prefix: formData.integration_type === 'crawler' ? formData.crawler_query_prefix.trim() : '',
        sro_config_id: formData.integration_type === 'sro' && formData.sro_config_id ? formData.sro_config_id : undefined,
      }

      if (integrationId) {
        await updateIntegration.mutateAsync({
          integrationId: parseInt(integrationId),
          data: payload,
        })
        toast({
          title: 'Integration updated',
          description: 'Your integration has been updated successfully.',
        })
      } else {
        await createIntegration.mutateAsync({
          assistantId: assistantId || '',
          data: payload,
        })
        toast({
          title: 'Integration created',
          description: 'Your integration has been created successfully.',
        })
      }

      navigate(`/app/assistants/${assistantId}`)
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save integration',
        variant: 'destructive',
      })
    }
  }

  const isLastStep = currentStep === 3
  const isFirstStep = currentStep === 1

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/app/assistants/${assistantId}`)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Assistant
        </Button>
        <h1 className="text-3xl font-bold">
          {integrationId ? 'Edit Integration' : 'Create New Integration'}
        </h1>
        <p className="text-muted-foreground mt-2">
          Follow the steps below to configure your integration
        </p>
      </div>

      {/* Stepper */}
      <div className="mb-8 p-6 bg-card rounded-lg border shadow-sm">
        <div className="flex items-start justify-between gap-4">
          {STEPS.map((step, index) => {
            const isActive = currentStep === step.number
            const isCompleted = currentStep > step.number

            return (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center min-w-[80px]">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center border-2 font-bold text-lg transition-colors ${
                      isCompleted
                        ? 'bg-primary border-primary text-primary-foreground'
                        : isActive
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-muted-foreground/30 text-muted-foreground'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="h-6 w-6" />
                    ) : (
                      <span>{step.number}</span>
                    )}
                  </div>
                  <div className="mt-3 text-center">
                    <div
                      className={`text-sm font-semibold ${
                        isActive ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {step.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {step.description}
                    </div>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 mt-6 rounded transition-colors ${
                      isCompleted ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {currentStep === 1 && (
            <Step1BasicInfo
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isEditing={!!integrationId}
            />
          )}
          {currentStep === 2 && (
            <Step2IntentRouting
              formData={formData}
              setFormData={setFormData}
              errors={errors}
            />
          )}
          {currentStep === 3 && (
            <Step3Configuration
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isEditing={!!integrationId}
            />
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={isFirstStep}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {isLastStep ? (
          <Button
            onClick={handleSubmit}
            disabled={createIntegration.isPending || updateIntegration.isPending}
          >
            {createIntegration.isPending || updateIntegration.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {integrationId ? 'Update Integration' : 'Create Integration'}
              </>
            )}
          </Button>
        ) : (
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  )
}
